import { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, ShieldCheck, Loader2, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { roleTermFor } from '../lib/roleTerm';
import { useAdminScopeTerms } from '../lib/adminScopeTerms';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ResidentApprovalsQueue
 * ----------------------
 * PM view : shows the PENDING_PM_REVIEW residents on the PM's own property.
 *           Actions: "Verify Resident" or "Reject".
 *
 * HQ view : shows the PENDING_HQ_APPROVAL residents across every property.
 *           Actions: "Activate Account" (sends welcome email + SMS) or
 *           "Reject".
 *
 * The endpoint auto-scopes by role, so the same component works on both
 * the Property Manager and the Company Admin dashboards.
 */
export function ResidentApprovalsQueue({ readOnly = false, propertyType }) {
  const terms = useAdminScopeTerms();
  const occupant = roleTermFor(propertyType);
  const [items, setItems] = useState([]);
  const [stage, setStage] = useState('pm');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // user_id being acted on
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/tenant/pending-approvals`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Unable to load approvals. Please retry.');
      const data = await res.json();
      setItems(data.items || []);
      setStage(data.stage || 'pm');
    } catch (e) {
      setError(e.message || 'Unable to load approvals. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (uid, endpoint, verb, successMsg) => {
    setBusy(uid);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/tenant/pending-approvals/${uid}/${endpoint}`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: verb }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Action failed.');
      toast.success(successMsg);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const title = stage === 'hq'
    ? `NECS HQ · ${terms.occupant} Activations`
    : `Property Manager · ${occupant} Approvals`;

  return (
    <Card
      className="p-4 sm:p-5 border-2 bg-white"
      style={{ borderColor: 'rgba(201, 169, 98, 0.4)' }}
      data-testid="resident-approvals-queue"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5" style={{ color: '#C9A962' }} />
          <h3
            data-testid="approvals-title"
            className="text-base font-bold uppercase tracking-wide"
            style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#000' }}
          >
            {title}
          </h3>
        </div>
        <Badge className="bg-[#FFD60A] text-black text-[10px]" data-testid="approvals-count">
          {items.length} pending
        </Badge>
      </div>

      <p className="text-xs text-zinc-500 mb-3" data-testid="approvals-description">
        {readOnly
          ? 'Read-only · Managed by Customer Service.'
          : stage === 'hq'
            ? 'Property Manager verification complete · Awaiting final activation.'
            : `${occupant} applications awaiting Property Manager verification.`}
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-zinc-500" role="status" data-testid="approvals-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div role="alert" className="space-y-2 text-sm text-red-700" data-testid="approvals-error">
          <p>{error}</p>
          <Button type="button" onClick={load} variant="outline" data-testid="approvals-retry">Retry</Button>
        </div>
      ) : items.length === 0 ? (
        <div className="p-4 rounded-md bg-zinc-50 border border-zinc-200 text-xs text-zinc-500 flex items-center gap-2" data-testid="approvals-empty">
          <UserPlus className="h-4 w-4" />
          Nothing waiting for you right now.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div
              key={it.user_id}
              className="p-3 rounded-md border border-zinc-200 bg-zinc-50"
              data-testid={`approval-row-${it.user_id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-zinc-900 break-words" data-testid={`approval-name-${it.user_id}`}>
                    {it.name || `${it.first_name || ''} ${it.last_name || ''}`.trim() || `Unnamed ${occupant.toLowerCase()}`}
                  </p>
                  <p className="text-[11px] text-zinc-600 break-all" data-testid={`approval-contact-${it.user_id}`}>
                    {it.email} · {it.phone || 'no phone'}
                  </p>
                  <p className="text-[11px] text-zinc-500 mt-0.5 break-words" data-testid={`approval-property-${it.user_id}`}>
                    {it.property_name || 'Property?'} · Unit {it.unit_number || '—'}
                    {stage === 'hq' && it.pm_verified_at && (
                      <> · PM verified {new Date(it.pm_verified_at).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {readOnly ? (
                    <Badge className="bg-zinc-200 text-zinc-700 text-[10px] uppercase tracking-wider" data-testid={`approval-readonly-${it.user_id}`}>
                      Managed by CS
                    </Badge>
                  ) : stage === 'pm' ? (
                    <Button
                      size="sm"
                      onClick={() => act(it.user_id, 'pm-verify', 'PM verified', `${occupant} verified — sent to NECS HQ.`)}
                      disabled={busy === it.user_id}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold"
                      data-testid={`approval-verify-${it.user_id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Verify {occupant}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      onClick={() => act(it.user_id, 'hq-activate', 'HQ activated', 'Account activated — welcome email + SMS sent.')}
                      disabled={busy === it.user_id}
                      className="bg-[#FFD60A] hover:bg-[#EAB308] text-black text-xs font-bold"
                      data-testid={`approval-activate-${it.user_id}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                      Activate Account
                    </Button>
                  )}
                  {!readOnly && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => act(it.user_id, 'reject', 'Rejected', `${stage === 'hq' ? 'Account' : occupant} application rejected.`)}
                      disabled={busy === it.user_id}
                      className="border-red-300 text-red-700 hover:bg-red-50 text-xs"
                      data-testid={`approval-reject-${it.user_id}`}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      Reject
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default ResidentApprovalsQueue;
